var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, FormControlLabel, InputAdornment, Radio, Stack, Typography } from '@mui/material';
import { round } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MetricTarget, StatusPermission } from '../../../enum';
import { useUserSessionAPI } from '../../app/selectors';
import { FormErrorText, FormField, NumberInput, RadioGroup } from '../../common';
import { BasePanel } from '../common/basePanel';
import { BaseVolumeTable } from '../common/baseTable';
import { QueuePanel } from '../common/queuePanel';
import { StepContainer, StepPanel } from '../common/stepContainer';
import { LocationTableEntryMode, WizardStep } from '../constants';
import { LocationTable } from '../locationTable';
import { useQueueAPI, useQueueLocation } from '../queue/selectors';
import { WorkType } from '../queue/types';
import { useEffectiveCapacityPermission, useIsPlanLocked, usePlanType, useWizardParams } from '../selectors';
import { WAPAccordion } from '../workArrivalPattern';
import WapTimezoneContext, { useWapTimezone } from '../workArrivalPattern/wapContext';
import { WapHoursMismatchDialog } from '../workArrivalPattern/wapHoursMismatchDialog';
import { LocationSelectionDialog } from './locationSelectionDialog';
import { useHoursMismatchDialog } from './selectors';
var DetailForm = function (_a) {
  var watch = _a.watch,
    clearErrors = _a.clearErrors,
    control = _a.control,
    isChatCurrency = _a.isChatCurrency,
    setValue = _a.setValue;
  var t = useTranslation('wizard', {
    keyPrefix: 'workVolume'
  }).t;
  var capacityPermission = useEffectiveCapacityPermission();
  var isPlanLocked = useIsPlanLocked();
  var disabled = capacityPermission !== StatusPermission.Edit || isPlanLocked;
  var metricTarget = watch('metricTarget');
  var patienceValue = watch('patience');
  var queueData = useQueueAPI()[0];
  var showMaxOcc = [MetricTarget.ServiceLevel, MetricTarget.ASA].includes(metricTarget);
  var isAbandonable = queueData.data && !queueData.data.isDeferrable && [WorkType.InboundCalls, WorkType.Chat, WorkType.Other].includes(queueData.data.worktypeId) && showMaxOcc;
  useEffect(function () {
    clearErrors === null || clearErrors === void 0 ? void 0 : clearErrors(['answered', 'threshold', 'targetAsa', 'targetOccupancy', 'maxOccupancy', 'patience', 'retryRate']);
  }, [metricTarget, clearErrors]);
  useEffect(function () {
    if (patienceValue == null) {
      setValue('retryRate', null);
      clearErrors === null || clearErrors === void 0 ? void 0 : clearErrors(['retryRate']);
    }
  }, [patienceValue, setValue, clearErrors]);
  return _jsxs(Stack, __assign({
    direction: "row",
    gap: 8,
    flexWrap: "wrap"
  }, {
    children: [metricTarget === MetricTarget.ASA && _jsx(FormField, __assign({
      required: true,
      name: "targetAsa",
      label: t('asaSec'),
      control: control
    }, {
      children: _jsx(NumberInput, {
        disabled: disabled,
        min: 1,
        max: 99999
      })
    })), (metricTarget === MetricTarget.ServiceLevel || metricTarget === MetricTarget.ASA) && _jsxs(_Fragment, {
      children: [_jsx(FormField, __assign({
        required: metricTarget === MetricTarget.ServiceLevel,
        name: "answered",
        label: t('answered'),
        control: control,
        extraLabel: metricTarget === MetricTarget.ASA ? t('optional') : ''
      }, {
        children: _jsx(NumberInput, {
          disabled: disabled,
          max: 100,
          InputProps: {
            endAdornment: _jsx(InputAdornment, __assign({
              position: "end"
            }, {
              children: "%"
            }))
          }
        })
      })), _jsx(FormField, __assign({
        required: metricTarget === MetricTarget.ServiceLevel,
        name: "threshold",
        label: t('threshold'),
        control: control,
        extraLabel: metricTarget === MetricTarget.ASA ? t('optional') : ''
      }, {
        children: _jsx(NumberInput, {
          disabled: disabled,
          max: 1000000
        })
      }))]
    }), metricTarget === MetricTarget.Occupancy && _jsx(FormField, __assign({
      required: true,
      name: "targetOccupancy",
      label: t('occupancy'),
      control: control
    }, {
      children: _jsx(NumberInput, {
        disabled: disabled,
        min: 1,
        max: 99.9999,
        decimal: 4,
        InputProps: {
          endAdornment: _jsx(InputAdornment, __assign({
            position: "end"
          }, {
            children: "%"
          }))
        }
      })
    })), isChatCurrency && _jsx(FormField, __assign({
      required: true,
      name: "chatConcurrency",
      label: t('chatConcurrency'),
      control: control
    }, {
      children: _jsx(NumberInput, {
        disabled: disabled,
        decimal: 1,
        min: 1,
        max: 99.9
      })
    })), isAbandonable && _jsxs(Stack, __assign({
      direction: "row",
      gap: 8,
      flexWrap: "wrap"
    }, {
      children: [_jsx(FormField, __assign({
        name: "patience",
        label: t('patience'),
        control: control
      }, {
        children: _jsx(NumberInput, {
          disabled: disabled,
          min: 0,
          max: 999999
        })
      })), _jsx(FormField, __assign({
        name: "retryRate",
        label: t('retryRate'),
        control: control
      }, {
        children: _jsx(NumberInput, {
          disabled: disabled || patienceValue == null,
          min: 0,
          max: 100,
          decimal: 2,
          InputProps: {
            endAdornment: _jsx(InputAdornment, __assign({
              position: "end"
            }, {
              children: "%"
            }))
          }
        })
      }))]
    })), showMaxOcc && _jsx(FormField, __assign({
      name: "maxOccupancy",
      label: t('maxOccupancy'),
      control: control
    }, {
      children: _jsx(NumberInput, {
        disabled: disabled,
        min: -1,
        max: 99.9999,
        decimal: 4,
        InputProps: {
          endAdornment: _jsx(InputAdornment, __assign({
            position: "end"
          }, {
            children: "%"
          }))
        }
      })
    }))]
  }));
};
var MetricTargetForm = function (props) {
  var t = useTranslation('wizard', {
    keyPrefix: 'workVolume'
  }).t;
  var metricTarget = props.watch('metricTarget');
  var capacityPermission = useEffectiveCapacityPermission();
  var isPlanLocked = useIsPlanLocked();
  var disabled = capacityPermission !== StatusPermission.Edit || isPlanLocked;
  return _jsxs(_Fragment, {
    children: [_jsx(FormField, __assign({
      name: "metricTarget",
      control: props.control
    }, {
      children: _jsxs(RadioGroup, __assign({
        row: true
      }, {
        children: [_jsx(FormControlLabel, {
          disabled: disabled,
          value: MetricTarget.ServiceLevel,
          control: _jsx(Radio, {}),
          label: t('serviceLevel')
        }), _jsx(FormControlLabel, {
          disabled: disabled,
          value: MetricTarget.ASA,
          control: _jsx(Radio, {}),
          label: t('asa')
        }), _jsx(FormControlLabel, {
          disabled: disabled,
          value: MetricTarget.Occupancy,
          control: _jsx(Radio, {}),
          label: t('occupancy')
        })]
      }))
    })), metricTarget && _jsx(DetailForm, {
      setValue: props.setValue,
      isChatCurrency: props.isChatCurrency,
      control: props.control,
      watch: props.watch,
      clearErrors: props.clearErrors
    })]
  });
};
export var WorkVolumeContent = function () {
  var _a, _b, _c, _d;
  var t = useTranslation('wizard', {
    keyPrefix: 'workVolume'
  }).t;
  var capacityPermission = useEffectiveCapacityPermission();
  var isPlanLocked = useIsPlanLocked();
  var userSession = useUserSessionAPI()[0];
  var queueData = useQueueAPI()[0];
  var queueLocations = useQueueLocation();
  var isChatCurrency = !!queueData.data && [WorkType.Chat, WorkType.SMS, WorkType.Social].includes(queueData.data.worktypeId);
  var _e = useFormContext(),
    control = _e.control,
    setValue = _e.setValue,
    watch = _e.watch,
    getValues = _e.getValues,
    clearErrors = _e.clearErrors;
  var errors = useFormState({
    name: 'queueLocations',
    control: control
  }).errors;
  var locationAllocationError = errors.queueLocations;
  var _f = useState(false),
    locationDialogOpen = _f[0],
    setLocationDialogOpen = _f[1];
  var timezone = useWapTimezone().timezone;
  var _g = useHoursMismatchDialog('hoursMismatch:location'),
    mismatchDialogOpen = _g.mismatchDialogOpen,
    mismatchedLocations = _g.mismatchedLocations,
    setMismatchDialogOpen = _g.setMismatchDialogOpen,
    fetchMismatch = _g.fetchMismatch;
  var step = useWizardParams().step;
  var handleLocationSaved = useCallback(function () {
    fetchMismatch(timezone);
  }, [fetchMismatch, timezone]);
  var isReview = step === WizardStep.Review;
  var isMultiGeoEnabled = !!((_c = (_b = (_a = userSession.data) === null || _a === void 0 ? void 0 : _a.orgs) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.multiGeoEnabled);
  var entryMode = watch().volumeEntryMode;
  var prevTotalsRef = useRef([]);
  var isPercentage = entryMode === LocationTableEntryMode.Percentage;
  var modeFormat = isPercentage ? {
    min: 0,
    max: 100,
    decimal: 2,
    suffix: '%'
  } : {
    min: 0,
    max: 9999999,
    decimal: 2
  };
  var handleFocus = useCallback(function () {
    var _a, _b;
    var currentTotals = (_b = (_a = getValues('volumes')) === null || _a === void 0 ? void 0 : _a.map(function (v) {
      return (v === null || v === void 0 ? void 0 : v.volume) == null ? null : Number(v.volume);
    })) !== null && _b !== void 0 ? _b : [];
    prevTotalsRef.current = currentTotals;
  }, [getValues]);
  var handleBlur = useCallback(function (periodIdx) {
    var _a, _b;
    if (isPercentage) return;
    var currentTotals = (_b = (_a = getValues('volumes')) === null || _a === void 0 ? void 0 : _a.map(function (v) {
      return (v === null || v === void 0 ? void 0 : v.volume) == null ? null : Number(v.volume);
    })) !== null && _b !== void 0 ? _b : [];
    var prevTotals = prevTotalsRef.current;
    var oldTotal = prevTotals[periodIdx];
    var newTotal = currentTotals[periodIdx];
    if (oldTotal === newTotal || oldTotal == null || newTotal == null || oldTotal === 0) return;
    var currentQueueLocations = getValues('queueLocations');
    if (!(currentQueueLocations === null || currentQueueLocations === void 0 ? void 0 : currentQueueLocations.length)) return;
    var hasChanges = false;
    var updated = currentQueueLocations.map(function (loc) {
      var _a, _b;
      return __assign(__assign({}, loc), {
        volumes: (_b = (_a = loc.volumes) === null || _a === void 0 ? void 0 : _a.map(function (v, idx) {
          if (idx !== periodIdx) return v;
          var rawVal = (v === null || v === void 0 ? void 0 : v.volume) == null ? null : Number(v.volume);
          if (rawVal === null) return v;
          hasChanges = true;
          return __assign(__assign({}, v), {
            volume: round(rawVal / oldTotal * newTotal, 0)
          });
        })) !== null && _b !== void 0 ? _b : []
      });
    });
    if (hasChanges) {
      setValue('queueLocations', updated, {
        shouldDirty: true
      });
    }
    prevTotalsRef.current[periodIdx] = newTotal;
  }, [getValues, setValue, isPercentage]);
  return _jsxs(_Fragment, {
    children: [_jsxs(Stack, __assign({
      spacing: 3
    }, {
      children: [_jsx(BaseVolumeTable, {
        name: "volume",
        averagePrecision: 0,
        min: 0,
        onFocus: handleFocus,
        onBlur: handleBlur
      }), _jsxs(Stack, __assign({
        spacing: 2,
        gap: 2
      }, {
        children: [!!queueLocations.length && _jsxs(_Fragment, {
          children: [_jsx(Typography, __assign({
            variant: "subtitle1"
          }, {
            children: t('volumeAllocation')
          })), isReview && _jsx(Typography, __assign({
            sx: {
              mt: '0!important'
            }
          }, {
            children: t('reviewLocationStepLink')
          })), _jsxs(Box, {
            children: [_jsx(LocationTable, __assign({
              name: "volume"
            }, modeFormat, {
              entryModeField: "volumeEntryMode",
              showTypeSums: true,
              showGroupHeader: true
            })), !!(locationAllocationError === null || locationAllocationError === void 0 ? void 0 : locationAllocationError.root) && _jsx(FormErrorText, {
              message: t('locationVolumeAllocationError')
            })]
          })]
        }), isMultiGeoEnabled && !isReview && _jsx(Box, {
          children: _jsx(Button, __assign({
            variant: "outlined",
            disabled: capacityPermission !== StatusPermission.Edit || isPlanLocked,
            onClick: function () {
              return setLocationDialogOpen(true);
            },
            sx: {
              textTransform: 'uppercase'
            }
          }, {
            children: t('chooseLocations')
          }))
        })]
      }))]
    })), _jsxs(Typography, __assign({
      variant: "subtitle1",
      sx: {
        mt: 2
      }
    }, {
      children: [t('targetService'), ' ']
    })), queueData.data && !queueData.data.isDeferrable && _jsx(MetricTargetForm, {
      setValue: setValue,
      control: control,
      isChatCurrency: isChatCurrency,
      watch: watch,
      clearErrors: clearErrors
    }), ((_d = queueData.data) === null || _d === void 0 ? void 0 : _d.isDeferrable) && _jsx(Stack, __assign({
      direction: "row",
      spacing: 4
    }, {
      children: _jsx(FormField, __assign({
        required: true,
        name: "factor",
        label: t('productivityFactor'),
        control: control
      }, {
        children: _jsx(NumberInput, {
          disabled: capacityPermission !== StatusPermission.Edit || isPlanLocked,
          min: 1,
          max: 100,
          InputProps: {
            endAdornment: _jsx(InputAdornment, __assign({
              position: "end"
            }, {
              children: "%"
            }))
          }
        })
      }))
    })), _jsx(LocationSelectionDialog, {
      open: locationDialogOpen,
      onClose: function () {
        return setLocationDialogOpen(false);
      },
      onSaved: handleLocationSaved
    }), _jsx(WapHoursMismatchDialog, {
      open: mismatchDialogOpen,
      mismatchedLocations: mismatchedLocations,
      onClose: function () {
        return setMismatchDialogOpen(false);
      }
    })]
  });
};
export var WorkVolumeStep = function () {
  var t = useTranslation('wizard', {
    keyPrefix: 'workVolume'
  }).t;
  var planType = usePlanType();
  var _a = useState(null),
    timezone = _a[0],
    setTimezone = _a[1];
  var timezoneContext = useMemo(function () {
    return {
      timezone: timezone,
      setTimezone: setTimezone
    };
  }, [timezone, setTimezone]);
  return _jsx(WapTimezoneContext.Provider, __assign({
    value: timezoneContext
  }, {
    children: _jsx(StepContainer, __assign({
      leftContent: _jsx(QueuePanel, {})
    }, {
      children: _jsxs(StepPanel, {
        children: [_jsx(BasePanel, __assign({
          title: t('name'),
          description: t('message', {
            planType: planType
          })
        }, {
          children: _jsx(WorkVolumeContent, {})
        })), _jsx(WAPAccordion, {})]
      })
    }))
  }));
};